import React, { useState, useContext, useMemo, useRef } from "react";
import {
  Row,
  Button,
  Grid,
  AuthContext,
  ViewContext,
  Loader,
  Message,
  ClassHelper,
} from "components/lib";
import Axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaste, faTrash } from "@fortawesome/free-solid-svg-icons";
import Style from "../../components/form/input/input.tailwind.js";
import MaterialReactTable from "material-react-table";

export function StaffVerification() {
  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext);
  const user = authContext.user;

  // Steps: 1=add staff, 2=compose email, 3=preview, 4=results
  const [step, setStep] = useState(1);
  const [staffQueue, setStaffQueue] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  // Manual entry fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  // CSV paste
  const [csvText, setCsvText] = useState("");
  const [parsedPreview, setParsedPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Email settings
  const [emailSubject, setEmailSubject] = useState(
    "Identity Verification Request"
  );
  const [emailBody, setEmailBody] = useState(
    "You have been requested to verify your identity. Please click the button below to begin the process."
  );
  const [replyTo, setReplyTo] = useState("");
  const [logoUrl, setLogoUrl] = useState("");

  const copyIcon = <FontAwesomeIcon icon={faPaste} />;
  const removeIcon = <FontAwesomeIcon icon={faTrash} />;

  const textStyle = ClassHelper(Style, {
    textbox: true,
  });

  const queueColumns = useMemo(
    () => [
      { accessorKey: "firstName", header: "First Name" },
      { accessorKey: "lastName", header: "Last Name" },
      { accessorKey: "email", header: "Email" },
    ],
    []
  );

  const resultsColumns = useMemo(
    () => [
      { accessorKey: "firstName", header: "First Name" },
      { accessorKey: "lastName", header: "Last Name" },
      { accessorKey: "email", header: "Email" },
      { accessorKey: "status", header: "Status" },
      {
        accessorKey: "idUrl",
        header: "Verification Link",
        Cell: ({ cell }) => {
          const url = cell.getValue();
          if (!url) return cell.row.original.error || "—";
          return (
            <span>
              <a href={url} target="_blank" rel="noreferrer">
                Link
              </a>{" "}
              <span
                style={{ cursor: "pointer" }}
                onClick={() => navigator.clipboard.writeText(url)}
              >
                {copyIcon}
              </span>
            </span>
          );
        },
      },
    ],
    []
  );

  // Plan gate
  if (user?.plan === "free") {
    return (
      <div>
        <Row>
          <Message
            type="warning"
            title="Paid Plan Required"
            text="Staff verification is only available on paid plans. Please upgrade to access this feature."
            buttonText="Upgrade Plan"
            buttonLink="/account/upgrade"
          />
        </Row>
      </div>
    );
  }

  // CSV parsing logic
  const parseCSV = (text) => {
    const lines = text.split("\n").filter((line) => line.trim() !== "");
    const parsed = [];

    for (const line of lines) {
      // Support comma, tab, or semicolon separated
      const parts = line.includes("\t")
        ? line.split("\t")
        : line.includes(";")
        ? line.split(";")
        : line.split(",");

      const trimmed = parts.map((p) => p.trim());

      if (trimmed.length >= 3 && trimmed[0] && trimmed[1] && trimmed[2]) {
        parsed.push({
          firstName: trimmed[0],
          lastName: trimmed[1],
          email: trimmed[2],
        });
      }
    }

    return parsed;
  };

  const handleParseCSV = () => {
    const parsed = parseCSV(csvText);
    if (parsed.length === 0) {
      viewContext.notification.show(
        "No valid entries found. Format: firstName,lastName,email (one per line)",
        "error"
      );
      return;
    }
    setParsedPreview(parsed);
  };

  const handleAddParsed = () => {
    if (parsedPreview) {
      setStaffQueue([...staffQueue, ...parsedPreview]);
      setCsvText("");
      setParsedPreview(null);
      viewContext.notification.show(
        `Added ${parsedPreview.length} staff members`,
        "success"
      );
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const parsed = parseCSV(text);
      if (parsed.length === 0) {
        viewContext.notification.show(
          "No valid entries found in file. Format: firstName,lastName,email",
          "error"
        );
        return;
      }
      setStaffQueue([...staffQueue, ...parsed]);
      viewContext.notification.show(
        `Added ${parsed.length} staff members from file`,
        "success"
      );
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleAddManual = () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      viewContext.notification.show(
        "Please fill in first name, last name, and email",
        "error"
      );
      return;
    }
    setStaffQueue([...staffQueue, { firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim() }]);
    setFirstName("");
    setLastName("");
    setEmail("");
  };

  const handleRemove = (index) => {
    setStaffQueue(staffQueue.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await Axios.post("/api/staff-verification/bulk", {
        staff: staffQueue,
        emailSettings: {
          subject: emailSubject,
          body: emailBody,
          replyTo: replyTo || undefined,
          logoUrl: logoUrl || undefined,
        },
      });
      setResults(response.data.data);
      setStep(4);
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || "Error creating staff verifications",
        "error"
      );
    }
    setLoading(false);
  };

  // Step 1: Add Staff
  if (step === 1) {
    return (
      <div>
        <h2>Staff Verification</h2>
        <p>Add your staff members for identity verification.</p>

        <Grid>
          <Row>
            <h3>Manual Entry</h3>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
              <div>
                <label>First Name</label>
                <input
                  type="text"
                  className={textStyle}
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <label>Last Name</label>
                <input
                  type="text"
                  className={textStyle}
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div>
                <label>Email</label>
                <input
                  type="email"
                  className={textStyle}
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <Button text="Add" action={handleAddManual} small />
            </div>
          </Row>

          <Row>
            <h3>Bulk Import (CSV)</h3>
            <p style={{ fontSize: "0.9em", color: "#666" }}>
              Paste CSV data with format: firstName, lastName, email (one per line)
            </p>
            <textarea
              className={textStyle}
              rows={6}
              placeholder={"John,Doe,john.doe@company.com\nJane,Smith,jane.smith@company.com"}
              value={csvText}
              onChange={(e) => {
                setCsvText(e.target.value);
                setParsedPreview(null);
              }}
              style={{ width: "100%", fontFamily: "monospace" }}
            />
            <br />
            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <Button text="Parse" action={handleParseCSV} small />
              <Button
                text="Upload CSV File"
                action={() => fileInputRef.current?.click()}
                small
                outline
              />
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                style={{ display: "none" }}
                onChange={handleFileUpload}
              />
            </div>

            {parsedPreview && (
              <div style={{ marginTop: "15px" }}>
                <Message
                  type="info"
                  text={`Found ${parsedPreview.length} staff members. Click "Add to Queue" to include them.`}
                />
                <Button text="Add to Queue" action={handleAddParsed} small />
              </div>
            )}
          </Row>
        </Grid>

        {staffQueue.length > 0 && (
          <Grid>
            <Row>
              <h3>Staff Queue ({staffQueue.length})</h3>
              <MaterialReactTable
                columns={queueColumns}
                data={staffQueue}
                enablePagination={true}
                enableRowActions
                positionActionsColumn="last"
                renderRowActions={({ row }) => (
                  <span
                    style={{ cursor: "pointer", color: "var(--sg-btn-danger, #8b1a15)" }}
                    onClick={() => handleRemove(row.index)}
                  >
                    {removeIcon}
                  </span>
                )}
              />
              <br />
              <div style={{ display: "flex", gap: "10px" }}>
                <Button
                  text="Clear All"
                  action={() => setStaffQueue([])}
                  small
                  outline
                />
                <Button
                  text="Next: Compose Email"
                  action={() => setStep(2)}
                  small
                />
              </div>
            </Row>
          </Grid>
        )}
      </div>
    );
  }

  // Step 2: Compose Email
  if (step === 2) {
    return (
      <div>
        <h2>Compose Verification Email</h2>
        <p>
          This email will be sent to each staff member with their verification
          link.
        </p>

        <Grid>
          <Row>
            <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
              <div>
                <label>Subject</label>
                <input
                  type="text"
                  className={textStyle}
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                />
              </div>
              <div>
                <label>Body</label>
                <textarea
                  className={textStyle}
                  rows={6}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Email body message"
                  style={{ width: "100%" }}
                />
              </div>
              <div>
                <label>Reply-To Email (optional)</label>
                <input
                  type="email"
                  className={textStyle}
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  placeholder="replies@yourcompany.com"
                />
              </div>
              <div>
                <label>Logo URL (optional)</label>
                <input
                  type="text"
                  className={textStyle}
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://yourcompany.com/logo.png"
                />
              </div>
            </div>
            <br />
            <div style={{ display: "flex", gap: "10px" }}>
              <Button text="Back" action={() => setStep(1)} small outline />
              <Button text="Preview Email" action={() => setStep(3)} small />
            </div>
          </Row>
        </Grid>
      </div>
    );
  }

  // Step 3: Preview
  if (step === 3) {
    return (
      <div>
        <h2>Preview & Confirm</h2>

        <Grid>
          <Row>
            <h3>Email Preview</h3>
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                padding: "30px",
                maxWidth: "600px",
                backgroundColor: "#ffffff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              {logoUrl && (
                <div style={{ textAlign: "center", marginBottom: "20px" }}>
                  <img
                    src={logoUrl}
                    alt="Logo"
                    style={{ maxHeight: "60px", maxWidth: "200px" }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
              <p
                style={{
                  fontSize: "0.85em",
                  color: "#888",
                  marginBottom: "5px",
                }}
              >
                <strong>Subject:</strong> {emailSubject}
              </p>
              {replyTo && (
                <p
                  style={{
                    fontSize: "0.85em",
                    color: "#888",
                    marginBottom: "15px",
                  }}
                >
                  <strong>Reply-To:</strong> {replyTo}
                </p>
              )}
              <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "15px 0" }} />
              <p style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>
                {emailBody}
              </p>
              <p style={{ whiteSpace: "pre-wrap", lineHeight: "1.6", fontSize: "0.9em", color: "#666" }}>
                {"\n\nThis service is provided by IdentityCheck"}
              </p>
              <div style={{ textAlign: "center", marginTop: "25px" }}>
                <span
                  style={{
                    display: "inline-block",
                    backgroundColor: "#3b82f6",
                    color: "#fff",
                    padding: "12px 30px",
                    borderRadius: "6px",
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  Confirm my identity
                </span>
              </div>
            </div>
          </Row>

          <Row>
            <h3>Staff List ({staffQueue.length})</h3>
            <Message
              type="info"
              text={`This email will be sent to ${staffQueue.length} staff member${staffQueue.length !== 1 ? "s" : ""}`}
            />
            <MaterialReactTable
              columns={queueColumns}
              data={staffQueue}
              enablePagination={true}
            />
            <br />
            <div style={{ display: "flex", gap: "10px" }}>
              <Button text="Back" action={() => setStep(2)} small outline />
              <Button
                text="Submit Verifications"
                action={handleSubmit}
                small
                loading={loading}
              />
            </div>
          </Row>
        </Grid>
      </div>
    );
  }

  // Step 4: Results
  if (step === 4) {
    if (loading) {
      return <Loader />;
    }

    return (
      <div>
        <h2>Verification Results</h2>

        {results && (
          <Grid>
            <Row>
              <Message
                type={results.summary.failed === 0 ? "success" : "warning"}
                text={`${results.summary.succeeded} of ${results.summary.total} verifications created successfully${results.summary.failed > 0 ? `. ${results.summary.failed} failed.` : "."}`}
              />
              <MaterialReactTable
                columns={resultsColumns}
                data={results.results}
                enablePagination={true}
              />
              <br />
              <Button
                text="Start New Batch"
                action={() => {
                  setStep(1);
                  setStaffQueue([]);
                  setResults(null);
                }}
                small
              />
            </Row>
          </Grid>
        )}
      </div>
    );
  }

  return null;
}
